<script setup lang="ts">
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Modal from '@/elements/Modal.vue';
import DynamicForm from '@/form/DynamicForm.vue';
import api from '@/api';
import BUS_EVENTS from '@/utils/bus-events';
import {
  DownloadResourcePayload,
  DownloadResourceProps,
} from '@/types/components/DownloadResource';
import { DynamicFormRef } from '@/types/form/dynamic-form';
import {
  applyDynamicFormAutofill,
  isCountryField,
  isStateField,
  updateRegionalFieldsByCountry,
} from '@/utils/dynamic-form-autofill';
import { getDynamicFormCheckboxDefaults } from '@/utils/dynamic-form-draft';
import { getOnlineMessageTracking } from '@/utils/online-message';

const props = defineProps<DownloadResourceProps>();
const bus = inject<any>('$bus');

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const dynamicFormRef = ref<DynamicFormRef>();
const activePayload = ref<DownloadResourcePayload>({});
const submitting = ref(false);
const feedbackMessage = ref('');
const captchaValue = ref('');
const captchaError = ref('');
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
const currentCountryValue = ref('');

const fields = computed(() => props.fields);
const activeResource = computed(() => activePayload.value.resource || {});

const getFieldValue = (
  fieldName: keyof NonNullable<DownloadResourceProps['fields']>,
  fallback = ''
) => {
  const field = fields.value?.[fieldName] as { value?: string } | undefined;
  return field?.value ?? fallback;
};

const resourceTitle = computed(() => activeResource.value.title || 'Selected resource');
const resourceDescription = computed(() => activeResource.value.description || '');
const resourceFileInfo = computed(() =>
  [activeResource.value.fileType || 'PDF', activeResource.value.fileSize]
    .filter(Boolean)
    .join(' · ')
);
const resourceIcon = computed(
  () => activeResource.value.coverImage?.src || 'data/media/images/download-resource-file.svg'
);
const resourceIconAlt = computed(
  () => activeResource.value.coverImage?.alt || resourceTitle.value || 'Resource'
);
const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.codeToken || data.code_token || '';
  captchaImage.value = data.src || '';
};

const getCookie = (name: string) => {
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : '';
};

const getSubmitHeaders = () => {
  const session = getCookie('GSAccessSession');
  return {
    Referer: window.location.href,
    ...(session ? { GSAccessSession: session } : {}),
  };
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};

const getDownloadUrl = () =>
  activePayload.value.downloadUrl || activeResource.value.downloadLink?.href || '';

const openDownload = () => {
  const url = getDownloadUrl();
  if (!url || url === '#') return false;

  window.open(url, activeResource.value.downloadLink?.target || '_self');
  return true;
};

const getCurrentCountryValue = () => {
  const values = dynamicFormRef.value?.getValues?.() || {};
  return (
    values.country || values.countryRegion || values.countryCode || values.country_region || ''
  );
};

const autofillForm = async () => {
  await nextTick();
  await applyDynamicFormAutofill(dynamicFormRef.value);
  dynamicFormRef.value?.updateFields(getDynamicFormCheckboxDefaults(props.fields?.form));
  currentCountryValue.value = getCurrentCountryValue();
};

const onFormChange = (_fieldType: string, fieldName: string, value: string) => {
  if (isCountryField(fieldName)) {
    currentCountryValue.value = value || '';
    updateRegionalFieldsByCountry(dynamicFormRef.value, value, undefined, true);
    return;
  }

  if (isStateField(fieldName)) {
    updateRegionalFieldsByCountry(
      dynamicFormRef.value,
      currentCountryValue.value || getCurrentCountryValue(),
      value
    );
  }
};
const onOpenForm = (
  payload: DownloadResourcePayload & { onSuccess?: () => void; onFailure?: () => void }
) => {
  activePayload.value = payload || {};
  feedbackMessage.value = '';
  captchaValue.value = '';
  captchaError.value = '';
  modalRef.value?.open();
  refreshCaptcha();
  void autofillForm();
};

const onClose = () => {
  captchaValue.value = '';
  captchaError.value = '';
  modalRef.value?.close();
};

const onCaptchaInput = () => {
  captchaError.value = '';
};

const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }

  captchaError.value = 'Please enter the security code shown on the right.';
  // return false;
  return true;
};

const onSubmit = async () => {
  if (submitting.value) return;
  const validation = await dynamicFormRef.value?.validate<Record<string, unknown>>();
  if (!validation?.valid) {
    dynamicFormRef.value?.focusFirstInvalid?.();
    return;
  }
  if (!validateCaptcha()) return;

  submitting.value = true;
  feedbackMessage.value = '';
  let ex: any;
  try {
    [, ex] = await api.downloadResource.submitOnlineMessage(
      null,
      {
        ...validation.data,
        ...getOnlineMessageTracking(validation.data),
        validateCode: captchaValue.value.trim(),
        codeToken: codeToken.value,
      },
      {
        headers: getSubmitHeaders(),
      }
    );
  } catch (error) {
    ex = error;
  } finally {
    submitting.value = false;
  }

  if (ex) {
    feedbackMessage.value = getErrorMessage(
      ex,
      getFieldValue('errorMessage', 'Submission failed. Please try again.')
    );
    await refreshCaptcha();
    bus?.emit(BUS_EVENTS.FORM_SUBMIT_FAILURE, { payload: activePayload.value, error: ex });
    (activePayload.value as DownloadResourcePayload & { onFailure?: () => void }).onFailure?.();
    return;
  }

  const hasDownload = openDownload();
  feedbackMessage.value = getFieldValue(
    'successMessage',
    hasDownload ? 'Thank you. Your download will start soon.' : 'Submit success.'
  );
  dynamicFormRef.value?.resetFields();
  captchaValue.value = '';
  captchaError.value = '';
  await refreshCaptcha();
  modalRef.value?.close();
  bus?.emit(BUS_EVENTS.FORM_SUBMIT_SUCCESS, { payload: activePayload.value });
  (activePayload.value as DownloadResourcePayload & { onSuccess?: () => void }).onSuccess?.();
};

onMounted(() => {
  refreshCaptcha();
  bus?.on(BUS_EVENTS.FORM_OPEN, onOpenForm);
});

onBeforeUnmount(() => {
  bus?.off(BUS_EVENTS.FORM_OPEN, onOpenForm);
});
</script>

<template>
  <Modal
    ref="modalRef"
    class="c-download-resource"
    content-class="c-download-resource__modal"
    :closable="true"
    :sticky="false"
    :is-full="false"
  >
    <div class="c-download-resource__panel">
      <div class="c-download-resource__header">
        <!-- <h2 class="c-download-resource__title">
          <strong>
            {{ getFieldValue('title', 'Download Resource') }}
          </strong>
        </h2> -->
        <p class="c-download-resource__description">
          {{
            getFieldValue('description', 'Please complete the form below to access this resource.')
          }}
        </p>
      </div>

      <!-- <div class="c-download-resource__summary">
        <img class="c-download-resource__summary-icon" :src="resourceIcon" :alt="resourceIconAlt" />
        <div class="c-download-resource__summary-content">
          <h3 class="c-download-resource__summary-title">{{ resourceTitle }}</h3>
          <p v-if="resourceFileInfo" class="c-download-resource__summary-info">
            {{ resourceFileInfo }}
          </p>
          <p v-if="resourceDescription" class="c-download-resource__summary-description">
            {{ resourceDescription }}
          </p>
        </div>
      </div> -->

      <form class="c-download-resource__form" @submit.prevent="onSubmit">
        <DynamicForm
          v-if="fields?.form"
          ref="dynamicFormRef"
          :form="fields.form"
          @change="onFormChange"
        />
        <div class="c-download-resource__captcha">
          <div
            :class="['c-download-resource__captcha-field', { 'has-error': Boolean(captchaError) }]"
          >
            <input
              v-model="captchaValue"
              type="text"
              inputmode="text"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              placeholder="security code on the right"
              aria-label="Security code"
              @input="onCaptchaInput"
            />
          </div>
          <img
            class="c-download-resource__captcha-image"
            :class="{ 'is-loading': captchaLoading }"
            :src="captchaImage"
            alt="Security code"
            role="button"
            @click="refreshCaptcha"
          />
          <p v-if="captchaError" class="c-download-resource__captcha-error">
            {{ captchaError }}
          </p>
        </div>
        <p v-if="feedbackMessage" class="c-download-resource__feedback">
          {{ feedbackMessage }}
        </p>
        <button
          class="c-download-resource__submit"
          :class="{ 'is-loading': submitting }"
          type="submit"
          :disabled="submitting"
        >
          <span
            v-if="submitting"
            class="c-download-resource__submit-spinner"
            aria-hidden="true"
          ></span>
          <span>{{
            submitting ? 'Submitting...' : getFieldValue('submitLabel', 'Download Now')
          }}</span>
        </button>
      </form>
    </div>
  </Modal>
</template>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-download-resource {
  z-index: 1000;

  .e-modal__mask {
    background: rgba(0, 0, 0, 0.66);
  }

  .e-modal__content.c-download-resource__modal {
    width: min(100% - 32px, 570px);
    height: auto;
    max-height: calc(100vh - 24px);
    max-height: calc(100dvh - 24px);
    padding: 10px 0 20px;
    border-radius: 8px;
    background: $white;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.22);
  }

  .e-modal__content-detail {
    max-height: calc(100vh - 72px);
    max-height: calc(100dvh - 72px);
    padding: 0;
  }

  &__panel {
    position: relative;
    padding: 22px 28px 30px;
  }

  &__header {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding-bottom: 22px;
    border-bottom: 1px solid #e4e7ec;
    background: $white;
  }

  &__title {
    margin: 0;
    color: #3f3f46;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
  }

  &__description {
    margin: 0;
    color: #8b92a1;
    font-size: 13px;
    line-height: 1.5;
    flex: 1 1 auto;
  }

  &__summary {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 14px;
    align-items: center;
    margin: 20px 0 22px;
    border-radius: 8px;
    background: #f1f5f2;
    padding: 14px 16px;
  }

  &__summary-icon {
    width: 42px;
    height: 42px;
    border-radius: 4px;
    object-fit: cover;
  }

  &__summary-title {
    margin: 0;
    overflow: hidden;
    color: #22252a;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__summary-info,
  &__summary-description {
    margin: 4px 0 0;
    color: #606575;
    font-size: 12px;
    line-height: 1.4;
  }

  &__summary-description {
    display: none;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__feedback {
    margin: 0;
    color: #b42318;
    font-size: 13px;
    line-height: 1.4;
  }

  &__captcha {
    display: grid;
    grid-template-columns: minmax(0, 206px) 118px;
    gap: 8px;
    align-items: start;
  }

  &__captcha-field {
    display: flex;
    align-items: center;
    min-height: 52px;
    border: 1px solid #aeb4be;
    border-radius: 6px;
    background: $white;
    padding: 0 14px;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }

  &__captcha-field:focus-within {
    border-color: #6f9a78;
    box-shadow: 0 0 0 2px rgba(111, 154, 120, 0.12);
  }

  &__captcha-field.has-error {
    border-color: #dc3545;
  }

  &__captcha-field input {
    width: 100%;
    border: 0;
    outline: 0;
    background: transparent;
    color: #22252a;
    font-size: 14px;
  }

  &__captcha-field input::placeholder {
    color: #9aa1ad;
  }

  &__captcha-image {
    width: 118px;
    height: 52px;
    border-radius: 6px;
    object-fit: cover;
    cursor: pointer;

    &.is-loading {
      opacity: 0.55;
      pointer-events: none;
    }
  }

  &__captcha-error {
    grid-column: 1 / -1;
    margin: -2px 0 0;
    color: #dc3545;
    font-size: 12px;
    line-height: 1.4;
  }

  &__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 44px;
    border: 0;
    border-radius: 999px;
    background: #6f9a78;
    color: $white;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
  }

  &__submit:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  &__submit-spinner {
    width: 16px;
    height: 16px;
    margin-right: 8px;
    border: 2px solid rgba($white, 0.45);
    border-top-color: $white;
    border-radius: 50%;
    animation: download-resource-submit-spin 700ms linear infinite;
  }
}

@keyframes download-resource-submit-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .c-download-resource {
    .e-modal__content.c-download-resource__modal {
      width: calc(100% - 24px);
      max-height: calc(100vh - 24px);
      max-height: calc(100dvh - 24px);
    }

    .e-modal__content-detail {
      max-height: calc(100vh - 72px);
      max-height: calc(100dvh - 72px);
    }

    &__panel {
      padding: 18px 18px 24px;
    }

    &__captcha {
      grid-template-columns: minmax(0, 1fr) 112px;
    }

    &__captcha-image {
      width: 112px;
    }
  }
}
</style>
